import DOMPurify from 'dompurify';

// ============================================================
// SECURITY UTILITIES
// Addresses OWASP Top 15 vulnerabilities:
// A01 Broken Access Control — role checks in store/components
// A02 Cryptographic Failures — passwords hashed server-side (see /server)
// A03 Injection / XSS — sanitize() used on all user-generated content
// A04 Insecure Design — payment flow enforces server-side checks
// A05 Security Misconfiguration — .env.example, no secrets in client
// A06 Vulnerable Components — lockfile, audit in CI
// A07 Auth Failures — token expiry, secure storage (see adapter)
// A08 Integrity Failures — CSP headers in server
// A09 Logging Failures — audit log in server layer
// A10 SSRF — all external calls through controlled API
// ============================================================

/**
 * Sanitize any user-supplied string before rendering to DOM.
 * Strips all HTML tags and dangerous attributes.
 */
export function sanitize(input: unknown): string {
  if (input === null || input === undefined) return '';
  const str = String(input);
  if (typeof window !== 'undefined') {
    return DOMPurify.sanitize(str, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] });
  }
  // SSR fallback
  return str.replace(/[<>"'&]/g, (c) => ({
    '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;', '&': '&amp;',
  }[c] ?? c));
}

/**
 * Truncate a string to a safe max length (prevents storage abuse).
 */
export function clamp(str: string, max: number): string {
  return str.slice(0, max);
}

// --- Validation ---

export function isValidEmail(email: string): boolean {
  // RFC 5322-ish, not perfect but practical
  return /^[^\s@]{1,64}@[^\s@]{1,255}\.[^\s@]{2,}$/.test(email.trim());
}

export function isValidPassword(pw: string): boolean {
  return pw.length >= 8 && pw.length <= 128;
}

export function isValidImageFile(file: File): { ok: boolean; error?: string } {
  const MAX_SIZE = 350 * 1024; // 350 KB
  const ALLOWED_TYPES = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  if (file.size > MAX_SIZE) return { ok: false, error: 'err_image_size' };
  if (!ALLOWED_TYPES.includes(file.type)) return { ok: false, error: 'err_image_format' };
  return { ok: true };
}

/**
 * Rate-limit helper (client-side, soft protection).
 * Real rate limiting must be enforced server-side.
 */
const rateLimitMap = new Map<string, number[]>();
export function checkRateLimit(key: string, maxCalls: number, windowMs: number): boolean {
  const now = Date.now();
  const calls = (rateLimitMap.get(key) ?? []).filter((t) => now - t < windowMs);
  if (calls.length >= maxCalls) return false;
  calls.push(now);
  rateLimitMap.set(key, calls);
  return true;
}

/**
 * Generate a cryptographically random ID (client-side UUID v4 fallback).
 */
export function generateId(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * Simple client-side password hash for demo localStorage adapter.
 * In production the server MUST use bcrypt/argon2 — never trust this.
 */
export function weakHash(str: string): string {
  let h = 0x811c9dc5;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return (h >>> 0).toString(16);
}

/**
 * Validate reward value.
 */
export function isValidReward(value: number): boolean {
  return Number.isFinite(value) && value >= 5 && value <= 999_999;
}

/**
 * Validate estimated hours.
 */
export function isValidHours(value: number): boolean {
  return Number.isFinite(value) && value >= 0.5 && value <= 2000;
}
